import browser from 'webextension-polyfill'
import { ethErrors } from 'eth-rpc-errors'

const EXTENSION_BUTTON_OFFSET_PX = 100
export const POPUP_BAR_HEIGHT_PX = 28
export const POPUP_BAR_HEIGHT_WIN_OS_PX = 39
export const EXTENSION_HEIGHT_PX = 600
export const POPUP_WIDTH_PX = 400
export const POPUP_WIDTH_WIN_OS_ADD_PX = 15
export const POPUP_REDUCED_HEIGHT_PX = 357
export const TOAST_HEIGHT_PX = 54

/**
 * Get main browser window
 * @returns {browser.Windows.Window} main window object
 */
export async function getBrowserWindow(): Promise<
  browser.Windows.Window | undefined
> {
  const allWindows: browser.Windows.Window[] = await browser.windows.getAll()
  return allWindows.find(window => window?.type === 'normal')
}

/**
 * Calculate popup top/left position in place of extension dropdown
 * @param {number} popupWidth width of the popup
 * @returns top and left value for the popup
 */
export async function getPosition(popupWidth: number): Promise<{
  top: number | undefined
  left: number | undefined
}> {
  const mainWindow = await getBrowserWindow()
  const left =
    (mainWindow?.width || screen.width) -
    EXTENSION_BUTTON_OFFSET_PX +
    (mainWindow?.left || 0) -
    popupWidth
  const top = EXTENSION_BUTTON_OFFSET_PX + (mainWindow?.top || 0)

  return { top, left }
}

/**
 * Create new popup window based on given params
 * @param url popup url
 * @param width popup width
 * @param height popup height (optional)
 * @returns window object of newly created popup
 */
export async function createPopup(
  url: string,
  width: number,
  height: number
): Promise<browser.Windows.Window | never> {
  const { left, top } = await getPosition(width)
  const window = await browser.windows.create({
    url,
    type: 'popup',
    height,
    width,
    left,
    top,
  })
  assertWindowId(window.id)
  if (window.state === 'fullscreen' && isChrome()) {
    return await browser.windows.update(window?.id, { state: 'normal' })
  }
  if (isFirefox() || (await isLinuxOs())) {
    await browser.windows.update(window?.id, {
      left,
      width,
      height,
      top,
    })
  }
  return window
}

/**
 * Ensures that the windowId parameter is a number, throws an
 * ethErrors.rpc.internal error if it's not.
 * @param {unknown} windowId - the id of the window
 * @throws {ethErrors.rpc.invalidParams} - throws if windowId is not a number
 */
export function assertWindowId(windowId: unknown): asserts windowId is number {
  if (windowId === undefined) {
    throw ethErrors.rpc.internal('Could not find the corresponding window.')
  }
}

/**
 * Check if current platform is Windows
 * @returns {boolean} boolean
 */
export async function isWindowsOs(): Promise<boolean> {
  const platformInfo = await browser.runtime.getPlatformInfo()
  return platformInfo.os === 'win'
}

/**
 * Check if current platform is Linux
 * @returns {boolean} boolean
 */
export async function isLinuxOs(): Promise<boolean> {
  const platformInfo = await browser.runtime.getPlatformInfo()
  return platformInfo.os === 'linux'
}

/**
 *  Get current value of the icon badge.
 *
 * @returns Promise object
 */
export async function getBadge(): Promise<string> {
  return await (isManifestV3()
    ? browser.action.getBadgeText({})
    : browser.browserAction.getBadgeText({}))
}

/**
 *  Set value of the icon badge. Value should be a max 4 char string or number. Passing empty string hide badge.
 *
 * @param value Value that should be presented in badge
 * @returns Promise object
 */
export async function setBadge(value: string | number): Promise<void> {
  const text = value + ''

  if (isManifestV3()) {
    await browser.action.setBadgeBackgroundColor({
      color: '#da8ba8',
    })

    return await browser.action.setBadgeText({
      text,
    })
  } else {
    await browser.browserAction.setBadgeBackgroundColor({
      color: '#da8ba8',
    })

    return await browser.browserAction.setBadgeText({
      text,
    })
  }
}

/**
 * Get current window object
 * @returns {browser.Windows.Window} current window object
 */
export async function getCurrentWindow(): Promise<browser.Windows.Window> {
  return await browser.windows.getCurrent()
}

/**
 * Gets the extension's url
 *
 * @returns url without a slash
 */
export function getURL(): string {
  return browser.runtime.getURL('').slice(0, -1)
}

/**
 * Gets version_name from the manifest
 *
 * @returns manifests' version_name
 */
export function getVersionName(): string | undefined {
  return (
    browser.runtime.getManifest().version_name ||
    browser.runtime.getManifest().version
  )
}

/**
 * Gets version_name from the manifest
 *
 * @returns manifests' version_name
 */
export function getExtensionName(): string | undefined {
  return (
    browser.runtime.getManifest().name ||
    browser.runtime.getManifest().short_name ||
    'Universal Profile'
  )
}

/**
 * Checks if manifest is of version 3
 */
export const isManifestV3 = () =>
  browser.runtime.getManifest().manifest_version === 3

/**
 * Checks if browser is Chrome
 */
export const isChrome = () => navigator.userAgent.includes('Chrome')

/**
 * Checks if browser is Firefox
 */
export const isFirefox = () => navigator.userAgent.includes('Firefox')
